import {
  Box,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material'
import TablePaginationDefault from './TablePaginationDefault'
import React from 'react'
import DataTableCellTypography from '../Typographys/DataTableCellTypography'

export type DataTableRowRender<T> = (row: T, index: number) => React.ReactNode

export type DataTableColumnRender<T> = (
  id: string,
  value: any,
  row: T
) => React.ReactNode

export interface IDataTableColumn<T> {
  id: string
  label: string | React.ReactNode
  minWidth?: number
  width?: number | string | undefined
  align?: 'right' | 'left' | 'center'
  sx?: SxProps<Theme>
  format?: (value: any) => string
  render?: DataTableColumnRender<T>
}

export type TDataTablePagination<T> = {
  items: T[]
  page: number
  rowsPerPage: number
  totalPages: number
  total: number
  info?: number
}

export type IDataTableTotals<T> = {
  label?: string
  sumColumnIds: string[]
  // permite customizar soma (ex: somar campo calculado)
  getValue?: (row: T, columnId: string) => number
  // permite formatar o total (ex: moeda)
  format?: (columnId: string, total: number) => string
  // em qual coluna mostrar o label "Total"
  labelColumnId?: string
}

export interface IDataTableProps<T> {
  key?: string | undefined
  columns: readonly IDataTableColumn<T>[]
  data: TDataTablePagination<T>
  page: number
  rowsPerPage: number
  hasNewCustomHeader?: boolean
  headerComponent?: React.ReactNode
  headerMobileComponent?: React.ReactNode
  hasPagination?: boolean
  headerBackgroundColor?: string
  enableBorders?: boolean
  pxInCell?: string
  isDividerHeader?: boolean
  totals?: IDataTableTotals<T>
  onItemClick?: (row: T) => void
  onPageChange: (page: number) => void
  onRowsPerPageChange: (rows: number) => void
  renderItemMobile?: DataTableRowRender<T>
}

const DataTable = <T,>({
  key,
  columns,
  data,
  page,
  rowsPerPage,
  hasNewCustomHeader,
  headerComponent,
  headerMobileComponent,
  hasPagination = true,
  headerBackgroundColor,
  enableBorders,
  pxInCell = '12px',
  isDividerHeader,
  totals,
  onItemClick,
  onPageChange,
  onRowsPerPageChange,
  renderItemMobile,
}: IDataTableProps<T>) => {
  const isSmallScreen = useMediaQuery((theme: any) =>
    theme.breakpoints.down('md')
  )
  const isRenderMobile = isSmallScreen && renderItemMobile

  const totalsMap = React.useMemo(() => {
    if (!totals) return null

    const getVal =
      totals.getValue ??
      ((row: T, columnId: string) => Number((row as any)[columnId] ?? 0))

    const map: Record<string, number> = {}

    for (const colId of totals.sumColumnIds) {
      map[colId] = data.items.reduce(
        (acc, row) => acc + (getVal(row, colId) || 0),
        0
      )
    }

    return map
  }, [totals, data.items])

  return (
    <Box key={key}>
      {isSmallScreen ? headerMobileComponent : headerComponent}
      {!isRenderMobile && (
        <TableContainer sx={{ width: '100%', overflow: 'hidden' }}>
          <Table
            stickyHeader
            aria-label="sticky table"
            sx={{ borderSpacing: '0px 10px', overflow: 'hidden' }}
          >
            <TableHead>
              <TableRow>
                {columns.map((column, index) => (
                  <TableCell
                    key={`${column.id}-${index}-${key ?? ''}`}
                    align={column.align}
                    style={{ minWidth: column.minWidth, width: column.width }}
                    sx={{
                      borderColor: isDividerHeader ? undefined : 'transparent',
                      py: '0px',
                      pb: isDividerHeader ? '16px' : '0px',
                      backgroundColor: headerBackgroundColor,
                      px: pxInCell,
                    }}
                  >
                    <Typography
                      color="#707275"
                      fontWeight={600}
                      fontSize="14px"
                      lineHeight="18px"
                    >
                      {column.label}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody sx={{ backgroundColor: 'transparent' }}>
              {data.items.map((row, rowIndex) => {
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={`${rowIndex.toString()}-${key ?? ''}`}
                    sx={{
                      cursor: onItemClick ? 'pointer' : undefined,
                      backgroundColor: 'white',
                    }}
                  >
                    {columns.map((column, index) => {
                      const value = (row as any)[column.id]
                      const isColumnFirst = index === 0
                      const isColumnLast = index === columns.length - 1
                      const borderFirstRadius = isColumnFirst ? '4px' : '0px'
                      const borderLastRadius = isColumnLast ? '4px' : '0px'
                      const borderDefault = enableBorders
                        ? '1px solid #DCDDDE'
                        : 'none'
                      const borderLeft =
                        enableBorders && isColumnFirst
                          ? '1px solid #DCDDDE'
                          : 'none'
                      const borderRight =
                        enableBorders && isColumnLast
                          ? '1px solid #DCDDDE'
                          : 'none'
                      return (
                        <TableCell
                          key={`${rowIndex}-${column.id}-${index}-${key ?? ''}`}
                          align={column.align}
                          width={column.width}
                          onClick={() => {
                            if (onItemClick) {
                              onItemClick(row)
                            }
                          }}
                          sx={{
                            borderTopLeftRadius: borderFirstRadius,
                            borderBottomLeftRadius: borderFirstRadius,
                            borderTopRightRadius: borderLastRadius,
                            borderBottomRightRadius: borderLastRadius,
                            borderTop: borderDefault,
                            borderBottom: borderDefault,
                            borderLeft,
                            borderRight,
                            px: pxInCell,
                            py: '10px',
                          }}
                        >
                          {column.render &&
                            column.render(column.id, value, row)}
                          {!column.render && (
                            <DataTableCellTypography sx={column.sx}>
                              {column.format
                                ? column.format(value)
                                : value?.toString() ?? ''}
                            </DataTableCellTypography>
                          )}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )
              })}
            </TableBody>

            {totals && totalsMap && (
              <tfoot>
                <TableRow sx={{ backgroundColor: 'white' }}>
                  {columns.map((column, index) => {
                    const isSum = totals.sumColumnIds.includes(column.id)
                    const labelColumnId = totals.labelColumnId ?? columns[0]?.id
                    const showLabelHere = column.id === labelColumnId

                    const totalValue = totalsMap[column.id]
                    const text = isSum
                      ? totals.format
                        ? totals.format(column.id, totalValue)
                        : totalValue.toString()
                      : showLabelHere
                        ? totals.label ?? 'Total'
                        : ''

                    return (
                      <TableCell
                        key={`total-${column.id}-${index}-${key ?? ''}`}
                        align={column.align}
                        sx={{
                          borderTop: '1px solid #DCDDDE',
                          px: pxInCell,
                          py: '12px',
                        }}
                      >
                        <Typography
                          fontWeight={700}
                          fontSize="14px"
                          color="#111827"
                        >
                          {text}
                        </Typography>
                      </TableCell>
                    )
                  })}
                </TableRow>
              </tfoot>
            )}
          </Table>
        </TableContainer>
      )}

      {isRenderMobile &&
        data.items.map((row, rowIndex) => {
          return renderItemMobile(row, rowIndex)
        })}

      {hasPagination && (
        <TablePaginationDefault
          count={data.total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          hasNewCustomHeader={hasNewCustomHeader}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}
    </Box>
  )
}

export default DataTable
